// Phase 14e-c-7 — shift section on Caregiver / Facility / Homecare detail.
//
// Mirrors <PayoutsCard> + <InvoicesCard> structure (paginated AdminTable
// with per-row action buttons). Persona-specific row buttons per 14e-c-7
// lock:
//   - Homecare client detail: cancel-by-operator + record-manual-payment
//   - Facility detail: cancel-by-operator only
//   - Caregiver detail: cancel-by-operator only
//
// Row-actions-only at v1 (no detail modal); operator drills deeper via
// audit trail. Per 14e-c-7 lock rationale.

import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Button, Card, CardHeader, Field, Input, StatusBadge } from '@truecare/ui';
import { shiftDisplayStatus } from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminTable, type AdminTableColumn } from './AdminTable';
import { OverrideActionButton } from './OverrideActionButton';

interface ShiftRow {
  id: string;
  status: string;
  scheduledStart: string;
  scheduledEnd: string;
  roleCode: string;
  setting: 'LTC' | 'HOMECARE';
  caregiverId: string;
  caregiverName: string;
  facilityId: string | null;
  facilityName: string | null;
  homecareClientId: string | null;
  homecareClientName: string | null;
  recipientId: string | null;
  recipientLabel: string | null;
  payRateAtBooking: string;
  billRateAtBooking: string;
  preAuthStatus:
    | 'NOT_REQUIRED'
    | 'PENDING'
    | 'AUTHORIZED'
    | 'FAILED'
    | 'CAPTURED'
    | 'RELEASED'
    | 'MANUAL_PAID';
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
}

export type ShiftsPersona = 'facility' | 'caregiver' | 'homecare';

export interface ShiftsCardProps {
  persona: ShiftsPersona;
  entityId: string;
}

export function ShiftsCard({ persona, entityId }: ShiftsCardProps) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const queryKey = ['admin', 'shifts', persona, entityId, { page, limit }] as const;
  const listQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const queryParam: Record<string, string> =
        persona === 'facility'
          ? { facilityId: entityId, page: String(page), pageSize: String(limit) }
          : persona === 'caregiver'
            ? { caregiverId: entityId, page: String(page), pageSize: String(limit) }
            : { homecareClientId: entityId, page: String(page), pageSize: String(limit) };
       
      const r = (await unwrap(
        api.GET('/api/v1/admin/shifts' as never, {
          params: { query: queryParam },
        } as never),
      )) as { items: ShiftRow[]; totalCount: number };
      return r;
    },
  });

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: ['admin', 'shifts'] });
  }

  // Counterparty column varies by persona.
  const counterpartyColumn: AdminTableColumn<ShiftRow> = (() => {
    if (persona === 'caregiver') {
      return {
        key: '__counterparty',
        header: 'Buyer',
        sortable: false,
        render: (r) =>
          r.facilityName ??
          r.homecareClientName ??
          r.recipientLabel ??
          '—',
      };
    }
    if (persona === 'facility') {
      return {
        key: '__counterparty',
        header: 'Professional',
        sortable: false,
        render: (r) => r.caregiverName,
      };
    }
    // homecare — show recipient + caregiver
    return {
      key: '__counterparty',
      header: 'Recipient / Professional',
      sortable: false,
      render: (r) => `${r.recipientLabel ?? '—'} / ${r.caregiverName}`,
    };
  })();

  const columns: AdminTableColumn<ShiftRow>[] = [
    {
      key: 'scheduledStart',
      header: 'Scheduled',
      width: 'w-44',
      render: (r) => new Date(r.scheduledStart).toLocaleString(),
    },
    counterpartyColumn,
    { key: 'roleCode', header: 'Role', width: 'w-20' },
    { key: 'setting', header: 'Setting', width: 'w-24' },
    {
      key: 'status',
      header: 'Status',
      width: 'w-36',
      // Doc 17 — an ended, unsigned shift reads "Awaiting sign-off" here too,
      // matching the Shifts page (the facility sees "Needs sign-off").
      render: (r) =>
        shiftDisplayStatus(r) === 'NEEDS_SIGNOFF' ? (
          <StatusBadge status="NEEDS_SIGNOFF" tone="warning" label="Awaiting sign-off" />
        ) : (
          <StatusBadge status={r.status} />
        ),
    },
    {
      key: '__actions',
      header: 'Actions',
      sortable: false,
      width: 'w-auto',
      render: (r) => (
        <ShiftRowActions persona={persona} shift={r} onMutation={invalidate} />
      ),
    },
  ];

  return (
    <Card padding="md" className="mt-6" data-testid={`${persona}-shifts-card`}>
      <CardHeader title="Shifts" />
      <div className="mt-4">
        <AdminTable
          columns={columns}
          rows={listQuery.data?.items ?? []}
          isLoading={listQuery.isPending}
          isError={listQuery.isError}
          emptyMessage="No shifts yet."
          page={page}
          limit={limit}
          totalCount={listQuery.data?.totalCount ?? 0}
          onPageChange={setPage}
          onLimitChange={(n) => {
            // 2026-07-23 audit (U4): resetting to page 1 is required — the
            // backends compute skip=(page-1)*pageSize with no clamping, so
            // growing the page size while on page 2+ can put skip past
            // totalCount and render "no results" over a populated list.
            setLimit(n);
            setPage(1);
          }}
        />
      </div>
    </Card>
  );
}

// ─── Row actions ────────────────────────────────────────────────────────────

function ShiftRowActions({
  persona,
  shift,
  onMutation,
}: {
  persona: ShiftsPersona;
  shift: ShiftRow;
  onMutation: () => void;
}) {
  const cancelByOperator = useMutation({
    mutationFn: (body: { reason: string; requeueForMatching: boolean }) =>
       
      unwrap(
        api.POST('/api/v1/admin/shifts/{id}/cancel-by-operator' as never, {
          params: { path: { id: shift.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const recordManualPayment = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
       
      unwrap(
        api.POST('/api/v1/admin/shifts/{id}/record-manual-payment' as never, {
          params: { path: { id: shift.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });

  const [paymentMechanic, setPaymentMechanic] = useState<string>('CHEQUE');
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [stripePmId, setStripePmId] = useState<string>('');
  // Mirror CancelByOperatorModal (shift-requests.$id.tsx): the operator opts in
  // to re-broadcast; default is NOT to re-broadcast. Wired via extraFields below
  // so the checkbox mentioned in `instructions` actually renders + is honored.
  const [requeueForMatching, setRequeueForMatching] = useState(false);

  // Gate on shift.status, not the cancelledAt proxy. Backend requireCancellable
  // allows only SCHEDULED / IN_PROGRESS — a COMPLETED shift never sets
  // cancelledAt, so `!cancelledAt` showed a Cancel button on completed rows that
  // always 409'd (invalid_state_transition). Mirror the portal's status check.
  const isCancellable = shift.status === 'SCHEDULED' || shift.status === 'IN_PROGRESS';
  // record-manual-payment (homecare Stripe escalation) requires an invoice to
  // exist — the backend refuses with `no_invoice_for_shift` otherwise. An
  // invoice is only created after the shift COMPLETES (webhook capture), so
  // don't surface the button until then. Previously any non-cancelled homecare
  // shift showed it → guaranteed 409 on SCHEDULED / IN_PROGRESS rows.
  // Defense-in-depth for the 2026-08-03 CRITICAL: a CAPTURED / MANUAL_PAID
  // shift is already fully paid, so the escalation button must not appear —
  // clicking it would attempt a second real Stripe charge (the backend now
  // also rejects this with `invoice_already_paid`, but don't surface the door).
  const isPaymentSettled =
    shift.preAuthStatus === 'CAPTURED' || shift.preAuthStatus === 'MANUAL_PAID';
  const showRecordManualPayment =
    persona === 'homecare' && shift.status === 'COMPLETED' && !isPaymentSettled;
  const isCancelled = shift.status === 'CANCELLED';

  return (
    <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
      {isCancellable && (
        <OverrideActionButton
          actionLabel="Cancel shift by operator"
          reasonLabel="Reason"
          instructions="Operator-initiated cancellation. Default behavior is NOT to re-broadcast; check the box if you want the auto-matcher to retry."
          extraFields={
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={requeueForMatching}
                onChange={(e) => setRequeueForMatching(e.target.checked)}
                data-testid={`shift-cancel-requeue-${shift.id}`}
              />
              Re-broadcast to care professionals after cancellation
            </label>
          }
          onConfirm={async ({ reason }) => {
            await cancelByOperator.mutateAsync({ reason, requeueForMatching });
            setRequeueForMatching(false);
          }}
          loading={cancelByOperator.isPending}
          data-testid={`shift-cancel-${shift.id}`}
        >
          Cancel
        </OverrideActionButton>
      )}
      {showRecordManualPayment && (
        <OverrideActionButton
          actionLabel="Record manual payment"
          reasonLabel="Operator note"
          instructions="Record an out-of-band payment captured by the operator (homecare Stripe escalation path)."
          extraFields={
            <>
              <Field label="Mechanic">
                <select
                  value={paymentMechanic}
                  onChange={(e) => setPaymentMechanic(e.target.value)}
                  className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                  data-testid={`shift-payment-mechanic-${shift.id}`}
                >
                  <option value="STRIPE_DIRECT_CHARGE">Stripe direct charge</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="EFT">EFT</option>
                  <option value="E_TRANSFER">e-Transfer</option>
                </select>
              </Field>
              <Field label="Amount ($)">
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  data-testid={`shift-payment-amount-${shift.id}`}
                />
              </Field>
              <Field label="External reference">
                <Input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  data-testid={`shift-payment-ref-${shift.id}`}
                />
              </Field>
              {paymentMechanic === 'STRIPE_DIRECT_CHARGE' && (
                <Field label="Stripe payment-method id (pm_...)">
                  <Input
                    type="text"
                    value={stripePmId}
                    onChange={(e) => setStripePmId(e.target.value)}
                    data-testid={`shift-stripe-pm-${shift.id}`}
                  />
                </Field>
              )}
            </>
          }
          onConfirm={async ({ reason }) => {
            const cents = Math.round(parseFloat(paymentAmount || '0') * 100);
            if (!Number.isFinite(cents) || cents <= 0) {
              throw new Error('Amount must be a positive dollar value.');
            }
            if (!paymentRef.trim()) {
              throw new Error('External reference is required.');
            }
            if (paymentMechanic === 'STRIPE_DIRECT_CHARGE' && !stripePmId.trim()) {
              throw new Error(
                'Stripe payment-method id required when mechanic = STRIPE_DIRECT_CHARGE.',
              );
            }
            const body: Record<string, unknown> = {
              mechanic: paymentMechanic,
              amountCents: cents,
              externalRef: paymentRef.trim(),
              operatorNote: reason,
            };
            if (paymentMechanic === 'STRIPE_DIRECT_CHARGE') {
              body['stripePaymentMethodId'] = stripePmId.trim();
            }
            await recordManualPayment.mutateAsync(body);
            setPaymentAmount('');
            setPaymentRef('');
            setStripePmId('');
          }}
          loading={recordManualPayment.isPending}
          data-testid={`shift-record-manual-payment-${shift.id}`}
        >
          Record manual payment
        </OverrideActionButton>
      )}
      {isCancelled && (
        <Button variant="ghost" size="sm" disabled>
          Cancelled
        </Button>
      )}
      {!isCancellable && !showRecordManualPayment && !isCancelled && (
        <span className="text-xs text-ink-muted" data-testid={`shift-no-actions-${shift.id}`}>
          No actions
        </span>
      )}
    </div>
  );
}
